import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useCashAccountOptions } from '@/features/accounts/api';

/**
 * WAVE 4D — THE CASH INVOICE, in the invoice header.
 *
 * The rule is the server's (invoices.service.ts, "the cash invoice"): a cash
 * invoice is paid at the counter, so posting it also creates and posts a
 * receipt for the full grand total into the chosen cash account, allocated to
 * the invoice; cancelling it reverses that receipt. The account select offers
 * the cash accounts (children of 1110 — the receipt's own "Received into"
 * list); "Company default" sends no account, and the server takes
 * `invoicing.default_cash_account` (or refuses when that is blank).
 */
export interface CashInvoiceState {
  isCash: boolean;
  /** '' = the company default. */
  cashAccountId: string;
}

export function cashInvoiceCreateBody(state: CashInvoiceState) {
  return state.isCash
    ? { isCash: true, ...(state.cashAccountId ? { cashAccountId: state.cashAccountId } : {}) }
    : {};
}

export function cashInvoicePatchBody(state: CashInvoiceState) {
  return { isCash: state.isCash, cashAccountId: state.isCash && state.cashAccountId ? state.cashAccountId : null };
}

export function CashInvoiceFields({
  state,
  onChange,
  disabled,
}: {
  state: CashInvoiceState;
  onChange: (patch: Partial<CashInvoiceState>) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const { options } = useCashAccountOptions();
  const held = state.cashAccountId !== '' && !options.some((option) => option.id === state.cashAccountId);
  return (
    <>
      <div className="grid gap-1.5">
        <Label htmlFor="inv-cash">{t('sales.fCashInvoice')}</Label>
        <label className="flex h-12 items-center gap-3 rounded-[11px] border border-input px-3 text-sm">
          <Checkbox
            id="inv-cash"
            checked={state.isCash}
            onCheckedChange={(checked) => onChange({ isCash: checked === true })}
            disabled={disabled}
          />
          <span className="text-muted-foreground">{t('sales.fCashInvoice')}</span>
        </label>
      </div>
      {state.isCash ? (
        <div className="grid gap-1.5">
          <Label htmlFor="inv-cash-account">{t('sales.fCashAccount')}</Label>
          <NativeSelect
            id="inv-cash-account"
            value={state.cashAccountId}
            onChange={(event) => onChange({ cashAccountId: event.target.value })}
            disabled={disabled}
          >
            <option value="">{t('sales.cashAccountDefault')}</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} {option.name}
              </option>
            ))}
            {held ? <option value={state.cashAccountId}>{state.cashAccountId}</option> : null}
          </NativeSelect>
        </div>
      ) : null}
    </>
  );
}

/** On a POSTED cash invoice: the receipt that paid it, linked. */
export function CashInvoiceBanner({
  receiptId,
  receiptCode,
}: {
  receiptId: string | null;
  receiptCode: string | null;
}) {
  const { t } = useTranslation();
  if (receiptId === null) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
    >
      <span>{t('sales.cashPaidBanner', { code: receiptCode ?? '' })}</span>
      <Link className="font-medium underline-offset-2 hover:underline" to={`/accounting/receipts/${receiptId}`}>
        {t('sales.cashPaidOpen')}
      </Link>
    </div>
  );
}
